import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import moment from 'moment';
import { PermissionType } from '@cbinsights/adminpermissionservice/adminpermissionservice';
import {
  getSectionPage,
  useGetAdminPermissionsForUser,
} from 'client/utils/permissions-validation/hooks/UseGetAdminPermissionsForUserQuery';
import { teamPermissions } from '../permissions/Team';
import { enterPendingRenewalStatus } from '../services/api/EnterPendingRenewalStatus';
import { exitPendingRenewalStatus } from '../services/api/ExitPendingRenewalStatus';
import { EXPIRED_PACKAGE } from '../utils/package';

export const usePendingRenewal = (isPausedFromServer: boolean) => {
  const enterPendingRenewalQuery = useMutation(enterPendingRenewalStatus);
  const exitPendingRenewalQuery = useMutation(exitPendingRenewalStatus);

  const { id } = useParams<{ id: string }>();
  const idTeam = Number(id);

  const { data } = useGetAdminPermissionsForUser();
  const section = getSectionPage(
    data,
    teamPermissions.pageId,
    teamPermissions.sections[1].sectionId
  );
  const hasChangePackagePermission = section.permissionTypes.includes(
    PermissionType.PUBLISH
  );

  const [isPendingRenewal, setIsPendingRenewal] = useState(isPausedFromServer);

  useEffect(() => {
    setIsPendingRenewal(isPausedFromServer);
  }, [isPausedFromServer]);
  const [isPausing, setIsPausing] = useState(false);
  const [isUnpausing, setIsUnpausing] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const pauseSubscription = async (): Promise<void> => {
    setIsPausing(true);
    try {
      await enterPendingRenewalQuery.mutateAsync({ id_team: idTeam });
      setIsPendingRenewal(true);
    } finally {
      setIsPausing(false);
    }
  };

  const unpauseSubscription = async (
    packageId: number,
    packageStart: string,
    packageEnd: string
  ): Promise<void> => {
    setIsUnpausing(true);
    try {
      await exitPendingRenewalQuery.mutateAsync({
        id_team: idTeam,
        package: {
          id_package: packageId,
          package_start: packageStart,
          package_end: packageEnd,
        },
      });
      setIsPendingRenewal(false);
    } finally {
      setIsUnpausing(false);
    }
  };

  const cancelSubscription = async (): Promise<void> => {
    setIsCancelling(true);
    try {
      await exitPendingRenewalQuery.mutateAsync({
        id_team: idTeam,
        package: {
          id_package: EXPIRED_PACKAGE,
          package_start: moment().format('YYYY-MM-DD HH:mm:ss'),
          package_end: moment().format('YYYY-MM-DD HH:mm:ss'),
        },
      });
      setIsPendingRenewal(false);
    } finally {
      setIsCancelling(false);
    }
  };

  return {
    isPendingRenewal,
    hasChangePackagePermission,
    pauseSubscription,
    unpauseSubscription,
    cancelSubscription,
    isPausing,
    isUnpausing,
    isCancelling,
  };
};

export default usePendingRenewal;
